import css from "./legal.module.css";

/**
 * Terms of Service — TV On Guitar LLC.
 *
 *  1. **Characterise the service accurately** — instructional material for
 *     members, not a catalogue of songs for sale. Section 2 and Section 4.
 *  2. **Make removal trivial and fast.** Section 6. In practice a rights holder
 *     sends a notice before anything else, and an operator who removes promptly
 *     very rarely sees more than that. This is the highest-value clause here.
 *  3. **Shift what is shiftable** — no redistribution licence to members
 *     (Section 5), creator warranties and indemnity (Section 7), the usual
 *     disclaimers (Sections 8 and 9).
 *
 * What it deliberately does **not** do is claim fair use. Asserting a defence
 * in a public document invites the argument and concedes that a defence is
 * needed. The Terms describe what the service is; they do not litigate.
 */

const EFFECTIVE = "September 1, 2026";
const CONTACT = "tvonguitar@gmail.com";
const ENTITY = "TV On Guitar LLC";

export default function TermsOfService() {
  return (
    <div className={css.page}>
      <h1 className={css.title}>Terms of Service</h1>
      <p className={css.meta}>Effective {EFFECTIVE}</p>

      <section className={css.section}>
        <h2 className={css.heading}>1. Who we are, and acceptance</h2>
        <p className={css.body}>
          This service is operated by {ENTITY} (“we”, “our”, “us”). By creating an
          account or accessing any material here, you agree to these Terms. If you
          do not agree, do not use the service.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>2. What this service is</h2>
        <p className={css.body}>
          This is a private, members-only library of original instructional
          material — guitar tablature, arrangements, practice notes and lesson
          videos — prepared by the creator for the purpose of teaching people how
          to play. Access is provided to members of the creator’s Patreon as part
          of that membership.
        </p>
        <p className={css.body}>
          Nothing here is sold. Membership contributions are made to the creator
          through Patreon in support of their ongoing work; they are not a
          purchase of, licence to, or payment for any musical composition, and no
          contribution amount is attributable to any particular file.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>3. Instructional transcriptions</h2>
        <p className={css.body}>
          Tablature and arrangements published here are the creator’s own
          independent transcriptions and arrangements, written by ear for
          instructional use. They are teaching aids: they are not reproductions of
          any published sheet music or engraving, they do not include lyrics, and
          they are not represented as official or authorised editions.
        </p>
        <p className={css.body}>
          Copyright in any underlying musical composition remains entirely with
          its owner. We claim no rights in those compositions, assert no
          affiliation with or endorsement by any rights holder, publisher, artist
          or game studio, and use their names only to identify the works being
          taught.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>4. Your licence to use the material</h2>
        <p className={css.body}>
          While your membership is active, you receive a personal, non-exclusive,
          non-transferable, revocable licence to use the material for your own
          private study and practice. That is the whole of it.
        </p>
        <ul className={css.list}>
          <li>Do not redistribute, republish, upload, mirror or share files.</li>
          <li>Do not post the material publicly, in whole or in part.</li>
          <li>Do not sell it, or use it in any commercial product or service.</li>
          <li>Do not share your account, or your access to it, with anyone else.</li>
        </ul>
        <p className={css.body}>
          These restrictions are a condition of access. Membership ends the
          licence; material downloaded beforehand remains subject to these Terms
          and to the rights of the underlying copyright owners.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>5. Performing and recording</h2>
        <p className={css.body}>
          Learning a piece is not the same as having permission to perform,
          record, stream or publish it. If you intend to do any of those things,
          you are responsible for obtaining the appropriate licences from the
          rights holders. Nothing here grants you any such right.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>6. Copyright concerns and takedown</h2>
        <p className={css.body}>
          We respect the rights of composers, publishers and artists, and we
          remove material promptly on request. If you own or represent the rights
          in a work and want something taken down, you do not need to send a
          formal notice or involve a lawyer — an email identifying the work and
          where it appears here is enough, and we will act on it.
        </p>
        <div className={css.callout}>
          <p className={css.body}>
            <strong>Takedown requests:</strong> {CONTACT}
          </p>
          <p className={css.body}>
            Please include the work concerned, the page or file where it appears,
            and a way to reach you. We aim to respond within three business days
            and to remove or disable access to identified material promptly.
          </p>
        </div>
        <p className={css.body}>
          Where the service hosts material uploaded by creators other than the
          operator, we act as a service provider under the Digital Millennium
          Copyright Act, and we terminate the accounts of repeat infringers.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>7. Creators who upload material</h2>
        <p className={css.body}>
          If you upload material, you represent that you own it or otherwise have
          the right to make it available, and that doing so does not infringe
          anyone’s rights. You retain ownership of what you upload, and you grant
          us only the licence needed to store it and deliver it to your members.
          You agree to indemnify {ENTITY} against claims arising from material you
          upload. We may remove any material at any time, for any reason.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>8. No warranty</h2>
        <p className={css.body}>
          The service and everything in it are provided “as is”, without warranty
          of any kind. We do not guarantee that the service will be available,
          that files will be accurate or complete, or that transcriptions are
          correct.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>9. Limitation of liability</h2>
        <p className={css.body}>
          To the fullest extent permitted by law, {ENTITY} is not liable for any
          indirect, incidental, special or consequential damages arising from your
          use of the service. Because no fee is charged for access to this
          service, our total liability to you for any claim is limited to one
          hundred United States dollars (US$100).
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>10. Suspension and termination</h2>
        <p className={css.body}>
          We may suspend or terminate access at any time, including for sharing
          material, sharing an account, or otherwise breaching these Terms. Your
          access also ends when your Patreon membership does.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>11. Changes</h2>
        <p className={css.body}>
          We may update these Terms. Material changes will be reflected in the
          effective date above, and continued use after a change means you accept
          the updated Terms.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>12. Governing law</h2>
        <p className={css.body}>
          These Terms are governed by the laws of the State of Georgia, United
          States, without regard to its conflict-of-laws rules. Any dispute
          arising from these Terms or your use of the service will be brought
          exclusively in the state or federal courts located in Georgia, and you
          consent to their jurisdiction.
        </p>
      </section>

      <section className={css.section}>
        <h2 className={css.heading}>13. Contact</h2>
        <p className={css.body}>
          {ENTITY} — {CONTACT}
        </p>
      </section>
    </div>
  );
}
